import { useMemo } from "react";
import Button from "@/components/button/button";
import { IShopItem, IUserBalance } from "../../types";
import { useTranslation } from "react-i18next";

import styles from "./ShopCard.module.scss";
import { formatPrice, getDiscountedPrice, isActiveDiscount } from "../../helpers";
import { stockTitleHelper } from "../../utils";

type Props = {
  item: IShopItem;
  userBalanceData: IUserBalance[];
  handleBuyButtonClick: (item: IShopItem) => void;
};

const ShopCard = ({ item, userBalanceData, handleBuyButtonClick }: Props) => {
  const { i18n, t } = useTranslation();
  const lng = i18n.language;
  const itemName = item.nameTranslations[lng] || item.name;
  const categoryName = item.category.translations[lng] || item.category.name;
  const activeDiscount = item.activeDiscount;
  const hasDiscount = isActiveDiscount(activeDiscount);
  const shouldShowStockBadge = item.showStock && typeof item.stock === "number";
  const discountedPrice = getDiscountedPrice(item.price, activeDiscount);

  const canBuyItem = useMemo(() => {
    const tokenBalance = parseFloat(
      userBalanceData.find((balance) => balance.token.id === item.token.id)?.balance ?? "0"
    );
    const isLimitReached = item.limitPerUser > 0 && item.consumedByMe >= item.limitPerUser;

    if (item.stock === 0) return false;
    if (isLimitReached) return false;

    return tokenBalance >= discountedPrice;
  }, [discountedPrice, item, userBalanceData]);

  return (
    <div className={styles.card}>
      <div className={styles.imageContainer}>
        <img src={item.image} alt={itemName} className={styles.gameImage} />

        <div className={styles.badgesOverlay}>
          {hasDiscount && <div className={styles.discountBadge}>-{Math.floor(Number(activeDiscount.value))}%</div>}

          {shouldShowStockBadge && <div className={styles.stockBadge}>{stockTitleHelper(item.stock, t)}</div>}
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.info}>
          <p className={styles.categoryName}>{categoryName}</p>
          <h3 className={styles.gameName}>{itemName}</h3>
        </div>

        <div className={styles.priceContainer}>
          <Button
            className={`${styles.priceButton} ${!canBuyItem ? styles.priceButtonDisabled : ""}`}
            label={
              <div className={`${styles.priceContent} ${hasDiscount ? styles.priceContentDiscount : ""}`}>
                <span className={styles.currentPrice}>
                  <img src={item.token.image} alt={item.token.name} className={styles.tokenImg} />

                  <span className={styles.price}>{formatPrice(discountedPrice)}</span>
                </span>

                {hasDiscount && <span className={styles.oldPrice}>{formatPrice(item?.price)}</span>}
              </div>
            }
            disabled={!canBuyItem}
            type={canBuyItem ? "primary" : "secondary"}
            onClick={() => handleBuyButtonClick(item)}
          />
        </div>
      </div>
    </div>
  );
};

export default ShopCard;
